<template lang="pug">
#resident-multiple-register(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 利用者情報一括登録
      .buttons
        button.btn(@click="onExampleModal" type="button") CSVファイルの仕様と作成例
        button.btn.template-btn(@click="onTemplateDownload" type="button") CSVファイルの雛形ダウンロード
    
    .box-body.register-body
      .note
        span 複数の利用者情報をCSVファイルで作成すると、一括登録することが出来ます。
        br
        span CSVファイルの仕様と作成例は、右上の「CSVファイルの仕様と作成例」をご確認ください。
        br
        span 新しくCSVファイルを作成する場合は、右上の「CSVファイルの雛形ダウンロード」をご利用ください。
      // inputs
      FormInputSelect(
        key-name="文字コード"
        name="encoding"
        v-model="encoding"
        :options="encodingOptions"
        :validation-rules="{ }"
        :styles="{ 'max-width': '540px' }")
      FormFile(
        key-name="CSVファイルの読み込み"
        name="csv-file"
        v-model="csv"
        :showDragAndDrop="false"
        :file-name.sync="fileName"
        mimetype=".csv"
        :validationRules="{}"
        height="32px"
        @input="inputCsv")
      
      ValidationAlert(v-model="0 < csvErrorMessages.length" :errorMessages="csvErrorMessages")

      // table
      .preview-table-content
        ResidentCsvTable(
          v-show="0 < residentItems.length"
          v-model="residentItems"
          :fields="residentFields"
          :isLoading="isLoading"
        )

    .box-footer.with-border.register-footer
      .buttons
        button.btn.register-btn(v-if="csvErrorMessages.length == 0 && 0 < residentItems.length" @click="showConfirmModal" type="button") 登録
        button.btn.register-btn.disable(v-else type="button" disabled) 登録
        button.btn.cancel-btn(@click="gotoResidentList" type="button") キャンセル

  OkCancelModal(v-model="showRegisterModal" title="利用者情報を登録しますか？" @ok="registerResident")
  OkModal(v-model="showRegisterSuccessModal" title="登録に成功しました。" @ok="gotoResidentList")
  ResidentMultipleRegisterExampleModal(ref="residentMultipleRegisterExampleModal")
  
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { ResidentListState, SendMessagesState } from "@/store/NeedRefreshes";
import { errorHandle } from "@/errorHandler";
import templateCsv from "@/assets/template/resident-register-template.csv";

import CustomValidate from "@/commons/utils/CustomValidate";
import { PcUrl } from "@/commons/constants/PcUrl";
import FacilitiesService, { FacilityGetModel } from "@/commons/services/Facilities";
import PlansService, { Plan, PlansGetModel, PlanType } from "@/commons/services/Plans";
import ResidentsService, { ResidentPostModel } from "@/commons/services/Residents";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import FileReaderWrapper from "@/commons/utils/FileReaderWrapper";
import RoomsService, { RoomGetModel } from "@/commons/services/Rooms";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import ResidentMultipleRegisterExampleModal from "@/pc/components/modals/ResidentMultipleRegisterExampleModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByStrings.vue";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";
import FormFile from "@/pc/components/forms/FormFile.vue";
import ResidentCsvTable, { ResidentCsvTableField, ResidentCsvTableItem } from "@/pc/containers/residents/ResidentCsvTable.vue";

/**
 * 利用者登録画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    ResidentMultipleRegisterExampleModal,
    PageLoading,
    ValidationAlert,
    FormInputSelect,
    FormFile,
    ResidentCsvTable
  },
  $_veeValidate: { validator: "new" }
})
export default class ResidentMultipleRegister extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;
  /** プランサービス */
  @Inject("PlansService") plansService!: PlansService;
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;
  /** 部屋サービス */
  @Inject("RoomsService") roomsService!: RoomsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 登録確認モーダル表示可否 */
  showRegisterModal: boolean = false;
  /** 登録完了モーダル表示可否 */
  showRegisterSuccessModal: boolean = false;

  /** グループIDとグループ名のMAP */
  groupNameMap: Map<string, number> = new Map();
  /** 部屋IDと部屋名のMAP */
  roomNameMap: Map<string, number> = new Map();

  /** 文字エンコーディング */
  encoding: string = "";
  /** 文字エンコーディング選択肢 */
  encodingOptions: Array<{ value: number | string; label: string }> = [];
  /** CSVファイル */
  csv: File | null = null;
  /** 表示用ファイル名 */
  fileName: string = "";

  /** プレビューテーブルの列名 */
  residentFields: Array<ResidentCsvTableField> = [];
  /** プレビューテーブルの各行 */
  residentItems: Array<ResidentCsvTableItem> = [];
  /** プレビューテーブル読込み中か否か */
  isLoading: boolean = false;

  /** エラーメッセージ */
  csvErrorMessages: string[] = [];

  /**
   * ### 文字エンコードの変更イベント
   *
   * - 現在読込んでいるCSVで再度CSV読込み処理を行わせる
   */
  @Watch("encoding")
  public changeEncoding(): void {
    this.inputCsv(this.csv);
    return;
  }

  /**
   * ### vue lifecycle created
   */
  public created(): void {
    CustomValidate.addCustomValidate(this.$validator);

    return;
  }

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   *
   * - プレビュー用のテーブルの列名を作る
   * - グループIDとグループ名の変換用にグループ情報のMAPを作る
   * - 部屋IDと部屋名の変換用に部屋情報のMAPを作る
   * - 文字エンコードの選択肢を作る
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const residentFields: Array<ResidentCsvTableField> = this.createResidentFields();
    this.residentFields = residentFields;

    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const groupNameMap: Map<string, number> = this.createGroupNameMap(groups);
    this.groupNameMap = groupNameMap;

    const rooms: Array<RoomGetModel> = await this.getRooms();
    const roomNameMap: Map<string, number> = this.createRoomNameMap(rooms);
    this.roomNameMap = roomNameMap;

    this.encodingOptions = [
      { value: "", label: "自動判別" },
      { value: "SHIFT_JIS", label: "SJIS（Shift-JIS）" },
      { value: "UTF-8", label: "UTF-8" }
    ];

    this.isBusy = false;

    return;
  }

  /**
   * ### プレビューテーブルの列名を作成する
   */
  public createResidentFields(): Array<ResidentCsvTableField> {
    let fields: Array<ResidentCsvTableField> = [];

    fields.push({ key: "rowNo", label: "行番号", sortable: false, sortDirection: "desc", class: "row-no" });
    fields.push({ key: "groupName", label: "利用者グループ", sortable: false, sortDirection: "desc", class: "group-name" });
    fields.push({ key: "room", label: "部屋名", sortable: false, sortDirection: "desc", class: "room" });
    fields.push({ key: "name", label: "利用者名", sortable: false, sortDirection: "desc", class: "resident-name" });
    fields.push({ key: "kanaName", label: "利用者名（ふりがな）", sortable: false, sortDirection: "desc", class: "resident-kana-name" });
    fields.push({ key: "gender", label: "性別", sortable: false, sortDirection: "desc", class: "gender" });
    fields.push({ key: "birthday", label: "生年月日", sortable: false, sortDirection: "desc", class: "birthday" });
    fields.push({ key: "tel", label: "電話番号", sortable: false, sortDirection: "desc", class: "tel" });
    fields.push({ key: "inDatetime", label: "入所日／入所予定日", sortable: false, sortDirection: "desc", class: "in-datetime" });
    fields.push({ key: "pickUpDatetime", label: "入所送迎時間", sortable: false, sortDirection: "desc", class: "pick-up-datetime" });
    fields.push({ key: "outDatetime", label: "退所日／退所予定日", sortable: false, sortDirection: "desc", class: "out-datetime" });
    fields.push({ key: "dropOffDatetime", label: "退所送迎時間", sortable: false, sortDirection: "desc", class: "drop-off-datetime" });
    fields.push({ key: "insuredNumber", label: "保険者番号", sortable: false, sortDirection: "desc", class: "insured-number" });
    fields.push({ key: "careInsuranceInsuredNumber", label: "介護保険被保険者番号", sortable: false, sortDirection: "desc", class: "care-insurance-insured-number" });

    return fields;
  }

  /**
   * ### 全ての利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### グループIDとグループ名のMAPを作成する
   */
  public createGroupNameMap(residentGroups: Array<ResidentGroupsGetModel>): Map<string, number> {
    let map: Map<string, number> = new Map<string, number>();
    for (const group of residentGroups) {
      map.set(group.name, group.id);
    }

    return map;
  }

  /**
   * ### 部屋を取得する
   */
  public async getRooms(): Promise<Array<RoomGetModel>> {
    const response: Array<RoomGetModel> = await this.roomsService.gets(userStore);
    return response;
  }

  /**
   * ### 部屋IDと部屋名のMAPを作成する
   */
  public createRoomNameMap(rooms: Array<RoomGetModel>): Map<string, number> {
    let map: Map<string, number> = new Map<string, number>();
    for (const room of rooms) {
      map.set(room.name, room.id);
    }

    return map;
  }

  /**
   * ### CSV読込み処理
   *
   * - プレビューテーブルの項目、CSVエラーメッセージを初期化する
   * - ファイルを読込んでテキスト情報とする
   * - テキスト情報の文字コードはUnicodeに変換する
   * - 変換できない(予期せぬ文字コード)の場合は読込めないCSVファイルとしてエラーメッセージを表示し、処理を中断する
   * - テキスト情報は改行コードで配列にし、行単位に扱う
   * - CSVの仕様により最終行には空行が存在する可能性があるのでその場合は削除する
   * - 1行目
   *   - 先頭列にSERVICE_USER_INFOという種別認識用の文字列が無ければエラーとし、処理を中断する
   * - 2行目
   *   - 項目を指す行なので項目名情報を作る
   *   - 項目必須チェック、項目重複チェックを行いエラーがあれば処理を中断する
   * - 3行目以降
   *   - プレビューテーブル表示用の情報を作る
   *   - 項目ごとにエラーチェックを行うが、エラーがあっても処理は中断しない
   */
  public async inputCsv(file: File | null): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "CSVファイル読込み中...";
    this.isLoading = true;

    this.residentItems = [];
    this.csvErrorMessages = [];

    if (file != null) {
      // CSVを読込んで文字列を取得する
      const allowdEncoding: string[] = ["UTF-8", "SHIFT_JIS"];
      const encoding: string | undefined = this.encoding ? this.encoding : undefined;
      const encodedText: { unicodeText: string; sourceEncoding: Encoding.Encoding } | null = await FileReaderWrapper.getUnicodeText(file, allowdEncoding, encoding); // eslint-disable-line

      // エンコードできなかった場合はエラー
      if (encodedText == null) {
        this.csvErrorMessages.push("読込み出来ないCSVファイルです");
        this.isBusy = false;
        return;
      }

      // 変換対象が許可していない文字コードの場合はsourceEncodingがnullなのでエラー
      if (encodedText.sourceEncoding == null) {
        this.csvErrorMessages.push("対応外の文字コードです。SJISで旧字体などを使用している場合は、文字コードを自動判別ではなくSJISにして再度読込ませてください。");
        this.isBusy = false;
        return;
      }

      // CSVを改行コードで行として分割する
      let lines: string[] = encodedText.unicodeText.split(/\r\n|\n|\r/g);

      // CSVの仕様で最後のレコードの後には改行はあってもなくてもいいので、最終行が空行の場合は削除する
      const lastLine: string = lines[lines.length - 1];
      if (!lastLine) {
        lines.pop();
      }

      console.debug("lines:", lines);

      // 利用者数上限を超える場合はエラーを返す
      // 利用者数上限が-1の場合は無制限なのでチェック対象外
      const residentLimit: number = await this.getResidentLimit();
      const count: number = await this.getResidentCount();

      if (residentLimit != -1 && residentLimit < count + lines.length - 2) {
        this.csvErrorMessages.push("利用者の登録数上限を超えるため登録できません");
        this.isBusy = false;
        return;
      }

      // CSVの形式は1行目：固定文字列、2行目：列名、3行目移行：利用者の情報なので3行未満ならエラー
      if (lines.length < 3) {
        this.csvErrorMessages.push("CSVファイルの形式が不正です。最低でも3行以上必要です");
        this.isBusy = false;
        return;
      }

      // 1行目の先頭に記載されている種別文字列チェック
      const firstLineRows: string[] = lines[0].split(",");

      console.debug(firstLineRows[0], "SERVICE_USER_INFO");

      if (String(firstLineRows[0]) != "SERVICE_USER_INFO") {
        this.csvErrorMessages.push("1行目はSERVICE_USER_INFOの記載が必要です");
        this.isBusy = false;
        return;
      }

      // 2行目のチェックと列番地と列名のMAPを作成する
      const rowAddressNameMap: Map<number, string> = this.createCsvRowAddressNameMap(lines[1]);
      if (0 < this.csvErrorMessages.length) {
        this.isBusy = false;
        return;
      }

      // 3行目以降の情報からテーブル用の情報を作るので1行目と2行目を削ったものを使用する
      let dataLines: string[] = lines.slice();
      dataLines.splice(0, 2);
      // 各行で項目数(カンマの数)の整合性チェックが必要なので2行目の項目数を作る
      const rowNameCount: number = lines[1].split(",").length;
      const tableItems: Array<ResidentCsvTableItem> = this.createTableItems(dataLines, rowAddressNameMap, this.groupNameMap, this.roomNameMap, rowNameCount);

      this.residentItems = tableItems;
    }

    this.isLoading = false;
    this.isBusy = false;

    return;
  }

  /**
   * ### 利用者登録上限数を返す
   *
   * - 施設のプラン毎に利用者登録上限値は異なる
   */
  public async getResidentLimit(): Promise<number> {
    let registrationLimit: number = 0;

    const facility: FacilityGetModel = await this.getFacility();

    // 現在プランが＋プランの場合は施設固有のプラン情報を返す
    if ((facility.planType == PlanType.LIGHT && facility.plan == Plan.PLAN_PLUS) || (facility.planType == PlanType.BASIC && facility.plan == Plan.PLAN_PLUS)) {
      if (facility.planDetail) {
        registrationLimit = facility.planDetail.residentLimit;
      }
    }
    // 現在プランが＋プラン以外の場合はマスター情報のプラン情報を返す
    else {
      const plans = await this.getPlan();

      for (const plan of plans) {
        if (facility.planType == plan.type && facility.plan == plan.plan) {
          registrationLimit = plan.residentLimit;
        }
      }
    }

    return registrationLimit;
  }

  /**
   * ### プランを取得する
   */
  public async getPlan(): Promise<Array<PlansGetModel>> {
    const response: Array<PlansGetModel> = await this.plansService.gets(userStore);
    return response;
  }

  /**
   * ### 施設取得
   */
  public async getFacility(): Promise<FacilityGetModel> {
    const response: FacilityGetModel = await this.facilitiesService.get(userStore);
    return response;
  }

  /**
   * ### 利用者数を取得する
   */
  public async getResidentCount(): Promise<number> {
    const count: number = await this.residentsService.getCount(userStore);
    return count;
  }

  /**
   * ### CSVの列番地と列名のMAPを作成する
   *
   * - 「,」区切りで配列化
   * - 配列から列名の番地のMAPを作る
   * - 項目名の重複チェック
   * - 項目名の必須チェック
   */
  public createCsvRowAddressNameMap(line: string): Map<number, string> {
    let map: Map<number, string> = new Map<number, string>();

    let existLastName: boolean = false;
    let existFirstName: boolean = false;
    let existGendar: boolean = false;
    let existBirthday: boolean = false;
    let existTel: boolean = false;
    let existRoom: boolean = false;
    let existGroupName: boolean = false;
    let existInDate: boolean = false;
    let existInTime: boolean = false;
    let existPickUpTime: boolean = false;
    let existOutDate: boolean = false;
    let existOutTime: boolean = false;
    let existDropOffTime: boolean = false;
    let existInsuredNumber: boolean = false;
    let existCareInsuranceInsuredNumber: boolean = false;

    const rows: string[] = line.split(",");
    for (let i = 0; i < rows.length; i++) {
      map.set(i, rows[i]);

      // 項目名の重複チェック
      switch (rows[i]) {
        case "last_name": {
          if (existLastName == true) {
            this.csvErrorMessages.push("2行目にlast_nameが重複しています。last_nameの列は1列だけにしてください");
          } else {
            existLastName = true;
          }
          break;
        }
        case "first_name": {
          if (existFirstName == true) {
            this.csvErrorMessages.push("2行目にfirst_nameが重複しています。first_nameの列は1列だけにしてください");
          } else {
            existFirstName = true;
          }
          break;
        }
        case "gendar": {
          if (existGendar == true) {
            this.csvErrorMessages.push("2行目にgendarが重複しています。gendarの列は1列だけにしてください");
          } else {
            existGendar = true;
          }
          break;
        }
        case "birthday": {
          if (existBirthday == true) {
            this.csvErrorMessages.push("2行目にbirthdayが重複しています。birthdayの列は1列だけにしてください");
          } else {
            existBirthday = true;
          }
          break;
        }
        case "telephone": {
          if (existTel == true) {
            this.csvErrorMessages.push("2行目にtelephoneが重複しています。telephoneの列は1列だけにしてください");
          } else {
            existTel = true;
          }
          break;
        }
        case "room_name": {
          if (existRoom == true) {
            this.csvErrorMessages.push("2行目にroom_nameが重複しています。room_nameの列は1列だけにしてください");
          } else {
            existRoom = true;
          }
          break;
        }
        case "group_name": {
          if (existGroupName == true) {
            this.csvErrorMessages.push("2行目にgroup_nameが重複しています。group_nameの列は1列だけにしてください");
          } else {
            existGroupName = true;
          }
          break;
        }
        case "in_date": {
          if (existInDate == true) {
            this.csvErrorMessages.push("2行目にin_dateが重複しています。in_dateの列は1列だけにしてください");
          } else {
            existInDate = true;
          }
          break;
        }
        case "in_time": {
          if (existInTime == true) {
            this.csvErrorMessages.push("2行目にin_timeが重複しています。in_timeの列は1列だけにしてください");
          } else {
            existInTime = true;
          }
          break;
        }
        case "pick_up_time": {
          if (existPickUpTime == true) {
            this.csvErrorMessages.push("2行目にpick_up_timeが重複しています。pick_up_timeの列は1列だけにしてください");
          } else {
            existPickUpTime = true;
          }
          break;
        }
        case "out_date": {
          if (existOutDate == true) {
            this.csvErrorMessages.push("2行目にout_dateが重複しています。out_dateの列は1列だけにしてください");
          } else {
            existOutDate = true;
          }
          break;
        }
        case "out_time": {
          if (existOutTime == true) {
            this.csvErrorMessages.push("2行目にout_timeが重複しています。out_timeの列は1列だけにしてください");
          } else {
            existOutTime = true;
          }
          break;
        }
        case "drop_off_time": {
          if (existDropOffTime == true) {
            this.csvErrorMessages.push("2行目にdrop_off_timeが重複しています。drop_off_timeの列は1列だけにしてください");
          } else {
            existDropOffTime = true;
          }
          break;
        }
        case "insured_number": {
          if (existInsuredNumber == true) {
            this.csvErrorMessages.push("2行目にinsured_numberが重複しています。insured_numberの列は1列だけにしてください");
          } else {
            existInsuredNumber = true;
          }
          break;
        }
        case "care_insurance_insured_number": {
          if (existCareInsuranceInsuredNumber == true) {
            this.csvErrorMessages.push("2行目にcare_insurance_insured_numberが重複しています。care_insurance_insured_numberの列は1列だけにしてください");
          } else {
            existCareInsuranceInsuredNumber = true;
          }
          break;
        }
        default: {
          break;
        }
      }
    }

    // 項目名の必須チェック
    if (existLastName == false) {
      this.csvErrorMessages.push("2行目にlast_nameの列は必須です");
    }
    if (existGendar == false) {
      this.csvErrorMessages.push("2行目にgenderの列は必須です");
    }
    if (existRoom == false) {
      this.csvErrorMessages.push("2行目にroom_nameの列は必須です");
    }

    return map;
  }

  /**
   * ### プレビューテーブル表示用のデータを作成する
   *
   * - 各行をカンマ区切りで配列に分ける
   * - CSV2行目の項目数とデータの配列数が合わない場合はエラーとする
   * - 配列の各項目が何の項目かをMAPから取得し、項目に応じて表示用データの項目に割り当てる
   * - 各項目毎にエラーチェックし、エラーがあれば表示用エラーメッセージに追加する
   * - エラーが発生しても処理は中断せず、全エラーを表示させる
   * - 利用者名については分割して保持しておき、その行のデータを全部読み込み終わった後必要に応じて結合して利用者名とする
   */
  public createTableItems(
    lines: string[],
    rowAddressNameMap: Map<number, string>,
    groupNameMap: Map<string, number>,
    roomNameMap: Map<string, number>,
    rowNameCount: number
  ): Array<ResidentCsvTableItem> {
    let items: Array<ResidentCsvTableItem> = [];

    for (let i = 0; i < lines.length; i++) {
      let item: ResidentCsvTableItem = {
        rowNo: i + 3,
        groupId: 1,
        groupName: "無所属",
        roomId: undefined,
        room: "",
        name: "",
        genderCode: 3,
        gender: "無回答",
        tel: "",
        birthday: null,
        inDate: null,
        inTime: null,
        pickUpTime: null,
        outDate: null,
        outTime: null,
        dropOffTime: null,
        insuredNumber: null,
        careInsuranceInsuredNumber: null
      };
      let lastKanaName: string | undefined = "";
      let firstKanaName: string | undefined = "";
      let lastName: string | undefined = "";
      let firstName: string | undefined = "";

      // 項目数の整合性チェック
      const rows: string[] = lines[i].split(",");
      if (rows.length != rowNameCount) {
        this.csvErrorMessages.push(`${i + 3}行目は不正なデータ行です。${i + 3}行目の項目数が2行目の項目数と異なります`);
        item.groupId = undefined;
        item.groupName = undefined;
        item.roomId = undefined;
        item.room = undefined;
        item.name = undefined;
        item.genderCode = undefined;
        item.gender = undefined;
        item.tel = undefined;
        item.birthday = undefined;
        item.inDate = undefined;
        item.inTime = undefined;
        item.pickUpTime = undefined;
        item.outDate = undefined;
        item.outTime = undefined;
        item.dropOffTime = undefined;
        item.insuredNumber = undefined;
        item.careInsuranceInsuredNumber = undefined;

        continue;
      }

      for (let j = 0; j < rows.length; j++) {
        const rowName: string | undefined = rowAddressNameMap.get(j);
        switch (rowName) {
          case "last_kana_name": {
            lastKanaName = rows[j] ? rows[j] : "";
            break;
          }
          case "first_kana_name": {
            firstKanaName = rows[j] ? rows[j] : "";
            break;
          }
          case "last_name": {
            // 必須チェック
            if (rows[j] == "") {
              this.csvErrorMessages.push(`${i + 3}行目のfirst_kana_nameは必須です`);
              lastName = undefined;
              break;
            }

            lastName = rows[j];
            break;
          }
          case "first_name": {
            // 名が空の場合はundefinedにする
            if (rows[j] == "") {
              firstName = undefined;
              break;
            }

            firstName = rows[j];
            break;
          }
          case "gendar": {
            let isError: boolean = false;

            // 性別が指定されていなければ無回答扱い
            if (rows[j] == "") {
              item.genderCode = 3;
              item.gender = "無回答";
              break;
            }
            // 数値チェック
            const genderCode: number = Number(rows[j]);
            if (isNaN(genderCode)) {
              this.csvErrorMessages.push(`${i + 3}行目のgendarは数値にしてください`);
              isError = true;
            }
            // コード値のチェック(1～3)
            if (genderCode < 1 || 3 < genderCode) {
              this.csvErrorMessages.push(`${i + 3}行目のgenderの値は1～3までの数値にしてください`);
              isError = true;
              break;
            }
            if (isError) {
              item.genderCode = undefined;
              item.gender = undefined;
              break;
            }

            item.genderCode = genderCode;
            item.gender = this.convertGender(genderCode);
            break;
          }
          case "birthday": {
            let isError: boolean = false;
            // 値無しを許容するので未入力であればnullを格納する
            if (rows[j] == "") {
              item.insuredNumber = null;
              break;
            }

            // 値が存在する場合はチェック
            if (rows[j]) {
              // 生年月日の桁数チェック
              if (rows[j].length != 8) {
                this.csvErrorMessages.push(`${i + 3}行目のbirthdayはyyyymmddの8桁の数値にしてください`);
                isError = true;
              }
              // 存在する日かチェック
              if (isError == false && !this.existDate(Number(rows[j] + "000000000"))) {
                this.csvErrorMessages.push(`${i + 3}行目のbirthdayは存在する年月日にしてください`);
                isError = true;
              }
              if (isError) {
                item.birthday = undefined;
                break;
              }
              item.birthday = Number(rows[j] + "000000000");
            }
            break;
          }
          case "telephone": {
            let isError: boolean = false;
            // 値無しを許容するので未入力であればnullを格納する
            if (rows[j] == "") {
              item.tel = null;
              break;
            }

            // 渡されてきたものに数字、-以外が含まれていればエラー
            const regex = new RegExp("^[0-9\\-]+$");
            if (!rows[j].match(regex)) {
              this.csvErrorMessages.push(`${i + 3}行目のtelephoneは半角数字または-のみにしてください`);
              isError = true;
            }
            // 桁数チェック
            if (13 < rows[j].length) {
              this.csvErrorMessages.push(`${i + 3}行目のtelephoneは13文字以内にしてください`);
              isError = true;
            }
            if (isError) {
              item.tel = undefined;
              break;
            }

            item.tel = rows[j];
            break;
          }
          case "room_name": {
            let isError: boolean = false;
            // 必須チェック
            if (rows[j] == "") {
              this.csvErrorMessages.push(`${i + 3}行目のroom_nameは必須です`);
              isError = true;
            }
            // 桁数チェック
            if (32 < rows[j].length) {
              this.csvErrorMessages.push(`${i + 3}行目のroom_nameは32文字以内にしてください`);
              isError = true;
            }
            // 存在する部屋名かチェック
            const roomId: number | undefined = roomNameMap.get(rows[j]);
            if (roomId == undefined) {
              this.csvErrorMessages.push(`${i + 3}行目のroom_nameは存在する部屋名にしてください`);
              isError = true;
            }
            if (isError) {
              item.room = undefined;
              break;
            }

            item.roomId = roomId;
            item.room = rows[j];
            break;
          }
          case "group_name": {
            // 空だったら無所属扱い
            if (rows[j] == "") {
              item.groupId = 1;
              item.groupName = "無所属";
              break;
            }
            // グループ名からグループIDが逆引き出来なかったらエラーとして扱う
            const groupId: number | undefined = groupNameMap.get(rows[j]);
            if (groupId == undefined) {
              this.csvErrorMessages.push(`${i + 3}行目のgroup_nameが存在しません。存在するグループ名を入力してください`);
              item.groupId = undefined;
              item.groupName = undefined;
              break;
            }

            item.groupId = groupId;
            item.groupName = rows[j];
            break;
          }
          default: {
            break;
          }
          case "in_date": {
            let isError: boolean = false;
            // 値無しを許容するので未入力であればnullを格納する
            if (rows[j] == "") {
              item.inDate = null;
              break;
            }
            // 桁数チェック
            if (rows[j].length != 8) {
              this.csvErrorMessages.push(`${i + 3}行目のin_dateはyyyymmddの8桁の数値にしてください`);
              isError = true;
            }
            // 存在する日かチェック
            if (isError == false && !this.existDate(Number(rows[j] + "000000000"))) {
              this.csvErrorMessages.push(`${i + 3}行目のin_dateは存在する年月日にしてください`);
              isError = true;
            }
            if (isError) {
              item.inDate = undefined;
              break;
            }

            item.inDate = Number(rows[j]);
            break;
          }
          case "in_time": {
            let isError: boolean = false;
            // 値無しを許容するので未入力であればnullを格納する
            if (rows[j] == "") {
              item.inTime = null;
              break;
            }
            // 桁数チェック
            if (!this.isTimeFormat(rows[j])) {
              this.csvErrorMessages.push(`${i + 3}行目のin_timeはhh:mmまたはh:mmの形式の文字列にしてください`);
              isError = true;
            }
            // 有効な時間かチェック
            if (isError == false && !this.validTime(rows[j])) {
              this.csvErrorMessages.push(`${i + 3}行目のin_timeは有効な時刻にしてください`);
              isError = true;
            }
            if (isError) {
              item.inTime = undefined;
              break;
            }

            item.inTime = rows[j];
            break;
          }
          case "pick_up_time": {
            let isError: boolean = false;
            // 値無しを許容するので未入力であればnullを格納する
            if (rows[j] == "") {
              item.pickUpTime = null;
              break;
            }
            // 桁数チェック
            if (!this.isTimeFormat(rows[j])) {
              this.csvErrorMessages.push(`${i + 3}行目のpick_up_timeはhh:mmまたはh:mmの形式の文字列にしてください`);
              isError = true;
            }
            // 有効な時間かチェック
            if (isError == false && !this.validTime(rows[j])) {
              this.csvErrorMessages.push(`${i + 3}行目のpick_up_timeは有効な時刻にしてください`);
              isError = true;
            }
            if (isError) {
              item.pickUpTime = undefined;
              break;
            }

            item.pickUpTime = rows[j];
            break;
          }
          case "out_date": {
            let isError: boolean = false;
            // 値無しを許容するので未入力であればnullを格納する
            if (rows[j] == "") {
              item.outDate = null;
              break;
            }
            // 桁数チェック
            if (rows[j].length != 8) {
              this.csvErrorMessages.push(`${i + 3}行目のout_dateはyyyymmddの8桁の数値にしてください`);
              isError = true;
            }
            // 存在する日かチェック
            if (isError == false && !this.existDate(Number(rows[j] + "000000000"))) {
              this.csvErrorMessages.push(`${i + 3}行目のout_dateは存在する年月日にしてください`);
              isError = true;
            }
            if (isError) {
              item.outDate = undefined;
              break;
            }

            item.outDate = Number(rows[j]);
            break;
          }
          case "out_time": {
            let isError: boolean = false;
            // 値無しを許容するので未入力であればnullを格納する
            if (rows[j] == "") {
              item.outTime = null;
              break;
            }
            // 桁数チェック
            if (!this.isTimeFormat(rows[j])) {
              this.csvErrorMessages.push(`${i + 3}行目のout_timeはhh:mmまたはh:mmの形式の文字列にしてください`);
              isError = true;
            }
            // 有効な時間かチェック
            if (isError == false && !this.validTime(rows[j])) {
              this.csvErrorMessages.push(`${i + 3}行目のout_timeは有効な時刻にしてください`);
              isError = true;
            }
            if (isError) {
              item.outTime = undefined;
              break;
            }

            item.outTime = rows[j];
            break;
          }
          case "drop_off_time": {
            let isError: boolean = false;
            // 値無しを許容するので未入力であればnullを格納する
            if (rows[j] == "") {
              item.dropOffTime = null;
              break;
            }
            // 桁数チェック
            if (!this.isTimeFormat(rows[j])) {
              this.csvErrorMessages.push(`${i + 3}行目のdrop_off_timeはhh:mmまたはh:mmの形式の文字列にしてください`);
              isError = true;
            }
            // 有効な時間かチェック
            if (isError == false && !this.validTime(rows[j])) {
              this.csvErrorMessages.push(`${i + 3}行目のdrop_off_timeは有効な時刻にしてください`);
              isError = true;
            }
            if (isError) {
              item.dropOffTime = undefined;
              break;
            }

            item.dropOffTime = rows[j];
            break;
          }
          case "insured_number": {
            let isError: boolean = false;
            // 値無しを許容するので未入力であればnullを格納する
            if (rows[j] == "") {
              item.insuredNumber = null;
              break;
            }
            // 渡されてきたものに半角数字が含まれていればエラー
            const regex = new RegExp("^[0-9]+$");
            if (!rows[j].match(regex)) {
              this.csvErrorMessages.push(`${i + 3}行目のinsured_numberは半角数字のみにしてください`);
              isError = true;
            }
            // 桁数チェック
            if (6 != rows[j].length) {
              this.csvErrorMessages.push(`${i + 3}行目のinsured_numberは6文字にしてください`);
              isError = true;
            }
            if (isError) {
              item.insuredNumber = undefined;
              break;
            }

            item.insuredNumber = rows[j];
            break;
          }
          case "care_insurance_insured_number": {
            let isError: boolean = false;
            // 値無しを許容するので未入力であればnullを格納する
            if (rows[j] == "") {
              item.careInsuranceInsuredNumber = null;
              break;
            }
            // 渡されてきたものに半角数字が含まれていればエラー
            const regex = new RegExp("^[a-zA-Z0-9]+$");
            if (!rows[j].match(regex)) {
              this.csvErrorMessages.push(`${i + 3}行目のcare_insurance_insured_numberは半角英数字のみにしてください`);
              isError = true;
            }
            // 桁数チェック
            if (10 != rows[j].length) {
              this.csvErrorMessages.push(`${i + 3}行目のcare_insurance_insured_numberは10文字にしてください`);
              isError = true;
            }
            if (isError) {
              item.careInsuranceInsuredNumber = undefined;
              break;
            }

            item.careInsuranceInsuredNumber = rows[j];
            break;
          }
        }
      }

      console.log(`lastKanaName:${lastKanaName}.`);
      console.log(`firstKanaName:${firstKanaName}.`);

      // 姓名の値によって利用者名（ふりがな）を作成する
      const regexByHiraganaAndNumbers = new RegExp("^[ぁ-んー　0-9０-９ ]*$");
      // 利用者名（姓）ふりがな、利用者名（名）ふりがなの両方が存在しない場合
      if (!lastKanaName && !firstKanaName) {
        item.kanaName = null;
      }
      // 利用者名（姓）ふりがなのみの場合
      else if (!lastKanaName && firstKanaName) {
        item.kanaName = firstKanaName;
        // 利用者名（ふりがな）の文字数チェック
        if (32 < item.kanaName.length) {
          this.csvErrorMessages.push(`${i + 3}行目のfirst_kana_nameは32文字以内にしてください`);
          item.kanaName = undefined;
        }
        // ひらがなと全角・半角数字のみを許容する
        else if (!regexByHiraganaAndNumbers.test(item.kanaName)) {
          this.csvErrorMessages.push(`${i + 3}行目のfirst_kana_nameはひらがなと数字のみにしてください`);
          item.kanaName = undefined;
        }
      }
      // 利用者名（名）ふりがなが空の場合
      else if (lastKanaName && !firstKanaName) {
        item.kanaName = lastKanaName;
        // 利用者名（ふりがな）の文字数チェック
        if (32 < item.kanaName.length) {
          this.csvErrorMessages.push(`${i + 3}行目のlast_kana_nameは32文字以内にしてください`);
          item.kanaName = undefined;
        }
        // ひらがなと全角・半角数字のみを許容する
        else if (!regexByHiraganaAndNumbers.test(item.kanaName)) {
          this.csvErrorMessages.push(`${i + 3}行目のlast_kana_nameはひらがなと数字のみにしてください`);
          item.kanaName = undefined;
        }
      }
      // 利用者名（姓）ふりがな、利用者名（名）ふりがなの両方が存在する場合
      else {
        item.kanaName = `${lastKanaName} ${firstKanaName}`;
        // 利用者名の文字数チェック
        if (32 < item.kanaName.length) {
          this.csvErrorMessages.push(`${i + 3}行目のlast_kana_nameとfirst_kana_nameの合計文字数は31文字以内にしてください`);
          item.kanaName = undefined;
        }
        // ひらがなと全角・半角数字のみを許容する
        else if (!regexByHiraganaAndNumbers.test(item.kanaName)) {
          this.csvErrorMessages.push(`${i + 3}行目のlast_kana_nameはひらがなと数字のみにしてください`);
          item.kanaName = undefined;
        }
      }

      // 姓名の値によって利用者名を作成する
      if (lastName == undefined) {
        item.name = undefined;
      } else if (firstName == undefined) {
        item.name = lastName;
        // 利用者名の文字数チェック
        if (item.name != undefined && 32 < item.name.length) {
          this.csvErrorMessages.push(`${i + 3}行目のlast_nameは32文字以内にしてください`);
          item.name = undefined;
        }
      } else {
        item.name = `${lastName} ${firstName}`;
        // 利用者名の文字数チェック
        if (item.name != undefined && 32 < item.name.length) {
          this.csvErrorMessages.push(`${i + 3}行目のlast_nameとfirst_nameの合計文字数は31文字以内にしてください`);
          item.name = undefined;
        }
      }

      // 入所日(日)と入所日(時間)が片方のみ入力されている場合はエラーを表示する
      if (!item.inDate && item.inTime) {
        this.csvErrorMessages.push(`${i + 3}行目のin_dateが入力されていないまたは無効ためin_timeは無効です`);
        item.inDate = undefined;
        item.inTime = undefined;
      } else if (item.inDate && !item.inTime) {
        this.csvErrorMessages.push(`${i + 3}行目のin_timeが入力されていないまたは無効ためin_dateは無効です`);
        item.inDate = undefined;
        item.inTime = undefined;
      }

      // 入所日(日)と入所日(時間)が未入力または無効で、入所送迎時間が入力されている場合はエラーを表示する
      if ((!item.inDate || !item.inTime) && item.pickUpTime) {
        this.csvErrorMessages.push(`${i + 3}行目のin_date、in_timeが入力されていないまたは無効ためpick_up_timeは無効です`);
        item.pickUpTime = undefined;
      }

      // 退所日(日)と退所日(時間)が片方のみ入力されている場合はエラーを表示する
      if (!item.outDate && item.outTime) {
        this.csvErrorMessages.push(`${i + 3}行目のout_dateが入力されていないまたは無効ためout_timeは無効です`);
        item.outDate = undefined;
        item.outTime = undefined;
      } else if (item.outDate && !item.outTime) {
        this.csvErrorMessages.push(`${i + 3}行目のout_timeが入力されていないまたは無効ためout_dateは無効です`);
        item.outDate = undefined;
        item.outTime = undefined;
      }

      // 退所日(日)と退所日(時間)が未入力または無効で、退所送迎時間が入力されている場合はエラーを表示する
      if ((!item.outDate || !item.outTime) && item.dropOffTime) {
        this.csvErrorMessages.push(`${i + 3}行目のout_date、out_timeが入力されていないまたは無効ためdrop_off_timeは無効です`);
        item.dropOffTime = undefined;
      }

      // 入所日時が退所日時より未来の場合はエラーを表示する
      let inDatetime = null;
      if (item.inDate && item.inTime) {
        const inTimes: string[] = item.inTime.split(":");
        inDatetime = Number(`${item.inDate}${inTimes[0].length == 2 ? inTimes[0] : "0" + inTimes[0]}${inTimes[1]}00000`);
      }
      let outDatetime = null;
      if (item.outDate && item.outTime) {
        const outTimes: string[] = item.outTime.split(":");
        outDatetime = Number(`${item.outDate}${outTimes[0].length == 2 ? outTimes[0] : "0" + outTimes[0]}${outTimes[1]}00000`);
      }
      if (inDatetime && outDatetime && outDatetime < inDatetime) {
        this.csvErrorMessages.push(`${i + 3}行目のin_date、in_timeがout_date、out_timeがより未来のためin_date、in_time、pickUpTime、out_date、out_time、dropOffTimeは無効です`);
        item.inDate = undefined;
        item.inTime = undefined;
        item.pickUpTime = undefined;
        item.outDate = undefined;
        item.outTime = undefined;
        item.dropOffTime = undefined;
      }

      items.push(item);
    }

    return items;
  }

  /**
   * ### 性別のコード値を文字列に変換する
   */
  public convertGender(code: number): string {
    switch (code) {
      case 1: {
        return `男性`;
      }
      case 2: {
        return `女性`;
      }
      case 3: {
        return `無回答`;
      }
      default: {
        return "";
      }
    }
  }

  /**
   * ### 存在する年月日かどうかを返す
   *
   * - Date型に変換後、年月日を比較して異なる場合はエラー
   */
  public existDate(datetime: number): boolean {
    const date: string = String(datetime);
    const year: number = Number(date.substr(0, 4));
    const month: number = Number(date.substr(4, 2)) - 1;
    const day: number = Number(date.substr(6, 2));
    const targetDate: Date = new Date(year, month, day);

    if (targetDate.getFullYear() != year) {
      return false;
    } else if (targetDate.getMonth() != month) {
      return false;
    } else if (targetDate.getDate() != day) {
      return false;
    }

    return true;
  }

  /**
   * ### 時刻形式かどうかを返す
   *
   * - 「:」が含まれていない場合はエラー
   * - 「:」が2文字目、3文字目にない場合はエラー(23:00、4:00の形式を許容するため)
   * - 全体文字数が4文字or5文字でない場合はエラー
   */
  public isTimeFormat(time: string): boolean {
    const separatorCharIndex: number = time.indexOf(":");

    if (separatorCharIndex == -1) {
      return false;
    }

    if (separatorCharIndex != 1 && separatorCharIndex != 2) {
      return false;
    }

    if (time.length != 4 && time.length != 5) {
      return false;
    }

    return true;
  }

  /**
   * ### 有効な時間かどうかを返す
   *
   * - h:mm形式、hh:mm形式の文字列であることが前提
   * - 形式チェックは本メソッド呼び出し元がチェックしておくこと
   * - 時刻形式を「:」を抜いた数値にし、24時(2400)未満であれば有効な時間と判断する
   */
  public validTime(time: string): boolean {
    const splitedTime: Array<string> = time.split(":");
    const joinTime: number = Number(splitedTime[0] + splitedTime[1]);

    if (0 < joinTime && joinTime < 2400) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * ### 登録確認モーダルを表示する
   */
  public async showConfirmModal(): Promise<void> {
    this.showRegisterModal = true;

    return;
  }

  /**
   * ### 利用者を登録する
   *
   * - Chromeのリクエスト数に上限があるので50人ずつ単位で並列でリクエストを送る
   * - 登録完了モーダルを表示する
   */
  public async registerResident(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    for (let i = 0; i * 50 < this.residentItems.length; i++) {
      const slicedItems: Array<ResidentCsvTableItem> = this.residentItems.slice(i * 50, i * 50 + 50);
      let promises: Array<Promise<void>> = [];

      for (const item of slicedItems) {
        const promise: Promise<void> = this.createRegisterPromise(item);
        promises.push(promise);
      }

      await Promise.all(promises).then((results: Array<any>) => {
        // エラーハンドル処理を実行する
        for (const result of results) {
          if (result) {
            errorHandle(result, this);
          }
        }
      });

      // 利用者一覧を再読み込みさせるようstoreを更新
      let residentList: ResidentListState = needRefreshesStore.residentList;
      residentList.residentList = true;
      needRefreshesStore.setResidentList(residentList);
      // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
      let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
      sendMessageState.sendMessages = true;
      needRefreshesStore.setSendMessage(sendMessageState);
    }

    this.isBusy = false;

    // 登録成功ダイアログを表示する
    this.showRegisterSuccessModal = true;

    return;
  }

  /**
   * ### 利用者登録Promiseを作成する
   */
  public async createRegisterPromise(item: ResidentCsvTableItem): Promise<void> {
    return new Promise(async (resolve: any) => {
      try {
        const requestBody: ResidentPostModel = this.createRequestBody(item);
        await this.residentsService.post(userStore, requestBody);
        resolve();
      } catch (error) {
        // Promise.all()で並列に実行されるので例外を投げなおすのではなく例外を戻り値として返す
        resolve(error);
      }
    });
  }

  /**
   * ### 利用者登録パラメータを作成する
   *
   * - 入所日／入所予定日、入所送迎時間を作成する
   *   - 入所日(日)、入所日(時)を結合して入所日／入所予定日にする
   *   - 入所日／入所予定日が存在し、入所送迎時間が存在すれば入所送迎時間にする
   * - 退所日／退所予定日、退所送迎時間を作成する
   *   - 退所日(日)、退所日(時)を結合して退所日／退所予定日にする
   *   - 退所日／退所予定日が存在し、退所送迎時間が存在すれば退所送迎時間にする
   * - リクエストボディを作成する
   */
  public createRequestBody(item: ResidentCsvTableItem): ResidentPostModel {
    let inDatetime: number | null = null;
    let pickUpDatetime: number | null = null;
    if (item.inDate && item.inTime) {
      const inTimes: string[] = item.inTime.split(":");
      inDatetime = Number(`${item.inDate}${inTimes[0].length == 2 ? inTimes[0] : "0" + inTimes[0]}${inTimes[1]}00000`);

      if (item.pickUpTime) {
        const pickUpTimes: string[] = item.pickUpTime.split(":");
        pickUpDatetime = Number(`${item.inDate}${pickUpTimes[0].length == 2 ? pickUpTimes[0] : "0" + pickUpTimes[0]}${pickUpTimes[1]}00000`);
      }
    }

    let outDatetime: number | null = null;
    let dropOffDatetime: number | null = null;
    if (item.outDate && item.outTime) {
      const outTimes: string[] = item.outTime.split(":");
      outDatetime = Number(`${item.outDate}${outTimes[0].length == 2 ? outTimes[0] : "0" + outTimes[0]}${outTimes[1]}00000`);

      if (item.dropOffTime) {
        const dropOffTimes: string[] = item.dropOffTime.split(":");
        dropOffDatetime = Number(`${item.outDate}${dropOffTimes[0].length == 2 ? dropOffTimes[0] : "0" + dropOffTimes[0]}${dropOffTimes[1]}00000`);
      }
    }

    // 入所・退所情報が1つでもあればそれを配列に格納し、なければ空配列を格納する
    let inAndOutDatetimes = [];
    if (inDatetime || outDatetime || pickUpDatetime || dropOffDatetime) {
      inAndOutDatetimes.push({
        inDatetime: inDatetime ? inDatetime : null,
        pickUpDatetime: pickUpDatetime ? pickUpDatetime : null,
        outDatetime: outDatetime ? outDatetime : null,
        dropOffDatetime: dropOffDatetime ? dropOffDatetime : null,
        roomId: item.roomId as number
      });
    }

    const requestBody: ResidentPostModel = {
      kanaName: item.kanaName ? this.$stringUtil.convertYen(item.kanaName) : "",
      name: item.name ? this.$stringUtil.convertYen(item.name) : "",
      birthday: item.birthday ? item.birthday : null,
      gender: item.gender as string,
      tel: item.tel ? item.tel : "",
      roomId: item.roomId as number,
      groupId: item.groupId as number,
      inAndOutDatetimes: inAndOutDatetimes,
      insuredNumber: item.insuredNumber ? item.insuredNumber : null,
      careInsuranceInsuredNumber: item.careInsuranceInsuredNumber ? item.careInsuranceInsuredNumber : null
    };

    return requestBody;
  }

  /**
   * ### 利用者一覧に遷移する
   */
  public gotoResidentList(): void {
    this.$router.push(PcUrl.ResidentList);

    return;
  }

  /**
   * ### 使用例モーダルを表示する
   */
  public onExampleModal(): void {
    const modal: any = this.$refs.residentMultipleRegisterExampleModal;
    if (modal) {
      modal.show();
    }

    return;
  }

  /**
   * ### テンプレートのダウンロード
   *
   * - 本来ならHTML5のa(href="csvのパス" download)でダウンロードさせたいが、vueだとファイルが存在しないになってしまう
   * - 解決策としてassetsにあるテンプレートのCSVを読込んでBlobに変換する
   * - aタグを内部的に作って、変換したBlobをダウンロードさせる
   */
  public onTemplateDownload(): void {
    let blob = new Blob([templateCsv], { type: "text/csv" });
    let link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = "resident-register-template.csv";
    link.click();

    return;
  }
}
</script>

<style lang="scss" scoped>
#resident-multiple-register {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .error-message-content {
    width: 100%;
    max-height: 300px;
    overflow-y: auto;
    margin: 0 20px;
  }

  .note {
    margin: 0 0 10px 0;
  }

  .preview-table-content {
    max-width: calc(100% - 30px);
    margin: 0 20px;
  }

  .template-btn {
    margin-left: 12px;
  }

  .register-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
